/**
 * API Keys Management Page
 * 
 * Allows users to manage API keys for external API access.
 * Keys can be created, viewed, revoked, and deleted.
 * 
 * @validates Requirements - External API access management
 */

import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAccountStore } from '../store/accountStore';
import {
  listApiKeys,
  createApiKey,
  revokeApiKey,
  deleteApiKey,
  type ApiKeyDisplay,
  type ApiKeyScope,
} from '../services/api-key.service';
import { API_KEY_SCOPES, type CreateApiKeyRequest } from '@budgira/shared';

// Icons
const PlusIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const KeyIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
  </svg>
);

const TrashIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const BanIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
  </svg>
);

const CopyIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const DocumentIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const ChevronUpIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
  </svg>
);

// Helper to format dates
const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Check if a key is expired
const isKeyExpired = (expiresAt?: string) => {
  if (!expiresAt) return false;
  return new Date(expiresAt) < new Date();
};

// Get effective status
const getEffectiveStatus = (apiKey: ApiKeyDisplay): 'active' | 'revoked' | 'expired' => {
  if (apiKey.status === 'revoked') return 'revoked';
  if (isKeyExpired(apiKey.expiresAt)) return 'expired';
  return 'active';
};

interface StatusBadgeProps {
  status: 'active' | 'revoked' | 'expired';
}

const StatusBadge = ({ status }: StatusBadgeProps) => {
  const { t } = useTranslation();
  
  const styles = {
    active: 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300',
    revoked: 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400',
    expired: 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300',
  };

  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${styles[status]}`}>
      {t(`apiKeys.${status}`)}
    </span>
  );
};

interface ApiKeyCardProps {
  apiKey: ApiKeyDisplay;
  onRevoke: (id: string) => void;
  onDelete: (id: string) => void;
}

const ApiKeyCard = ({ apiKey, onRevoke, onDelete }: ApiKeyCardProps) => {
  const { t } = useTranslation();
  const { accounts } = useAccountStore();
  const [isExpanded, setIsExpanded] = useState(false);
  const effectiveStatus = getEffectiveStatus(apiKey);

  // Get account names for display
  const getAccountNames = () => {
    if (apiKey.accountIds.length === 0) return t('apiKeys.allAccounts');
    const accountNames = apiKey.accountIds
      .map(id => accounts.find(a => a.id === id)?.name)
      .filter(Boolean);
    return accountNames.length > 0 
      ? accountNames.join(', ')
      : t('apiKeys.selectedAccounts', { count: apiKey.accountIds.length });
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
      {/* Header */}
      <div className="p-4 flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-slate-400 dark:text-slate-500">
              <KeyIcon />
            </span>
            <h3 className="font-semibold text-slate-900 dark:text-slate-100 truncate">
              {apiKey.name}
            </h3>
            <StatusBadge status={effectiveStatus} />
          </div>
          <div className="flex items-center gap-4 text-sm text-slate-500 dark:text-slate-400">
            <span>
              {t('apiKeys.keyPrefix')}: <code className="bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 rounded text-xs font-mono">{apiKey.keyPrefix}...</code>
            </span>
            <span>
              {t('apiKeys.createdAt')}: {formatDate(apiKey.createdAt)}
            </span>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-1 ml-4">
          {effectiveStatus === 'active' && (
            <button
              onClick={() => onRevoke(apiKey.id)}
              className="p-2 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/50 text-slate-400 hover:text-amber-600 dark:hover:text-amber-400 transition-colors"
              title={t('apiKeys.revoke')}
            >
              <BanIcon />
            </button>
          )}
          <button
            onClick={() => onDelete(apiKey.id)}
            className="p-2 rounded-lg hover:bg-rose-100 dark:hover:bg-rose-900/50 text-slate-400 hover:text-rose-600 dark:hover:text-rose-400 transition-colors"
            title={t('apiKeys.delete')}
          >
            <TrashIcon />
          </button>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 transition-colors"
          >
            {isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
          </button>
        </div>
      </div>

      {/* Expanded details */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-slate-100 dark:border-slate-700 pt-4 space-y-3 text-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <span className="text-slate-500 dark:text-slate-400">{t('apiKeys.accounts')}:</span>
              <p className="text-slate-700 dark:text-slate-200">{getAccountNames()}</p>
            </div>
            <div>
              <span className="text-slate-500 dark:text-slate-400">{t('apiKeys.expiresAt')}:</span>
              <p className="text-slate-700 dark:text-slate-200">
                {apiKey.expiresAt ? formatDate(apiKey.expiresAt) : t('apiKeys.noExpiration')}
              </p>
            </div>
            <div>
              <span className="text-slate-500 dark:text-slate-400">{t('apiKeys.lastUsed')}:</span>
              <p className="text-slate-700 dark:text-slate-200">
                {apiKey.lastUsedAt ? formatDate(apiKey.lastUsedAt) : t('apiKeys.neverUsed')}
              </p>
            </div>
          </div>
          
          <div>
            <span className="text-slate-500 dark:text-slate-400">{t('apiKeys.scopes')}:</span>
            <div className="flex flex-wrap gap-1 mt-1">
              {apiKey.scopes.map(scope => (
                <span
                  key={scope}
                  className="px-2 py-0.5 text-xs bg-violet-100 dark:bg-violet-900/50 text-violet-700 dark:text-violet-300 rounded"
                >
                  {t(`apiKeys.availableScopes.${scope}`)}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface CreateKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (key: string, apiKey: ApiKeyDisplay) => void;
}

const CreateKeyModal = ({ isOpen, onClose, onCreated }: CreateKeyModalProps) => {
  const { t } = useTranslation();
  const { accounts } = useAccountStore();
  
  const [name, setName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<ApiKeyScope[]>([
    'operations:read',
    'operations:create',
    'accounts:read',
    'categories:read',
    'counterparties:read',
    'payment-methods:read',
  ]);
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [allAccounts, setAllAccounts] = useState(true);
  const [expiresIn, setExpiresIn] = useState<string>('never');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Group scopes by category
  const scopesByCategory = API_KEY_SCOPES.reduce((acc, { scope, category }) => {
    if (!acc[category]) acc[category] = [];
    acc[category].push(scope);
    return acc;
  }, {} as Record<string, ApiKeyScope[]>);

  const handleScopeToggle = (scope: ApiKeyScope) => {
    setSelectedScopes(prev => 
      prev.includes(scope) 
        ? prev.filter(s => s !== scope)
        : [...prev, scope]
    );
  };

  const handleCategoryToggleAll = (_category: string, scopes: ApiKeyScope[]) => {
    const allSelected = scopes.every(s => selectedScopes.includes(s));
    if (allSelected) {
      setSelectedScopes(prev => prev.filter(s => !scopes.includes(s)));
    } else {
      setSelectedScopes(prev => [...new Set([...prev, ...scopes])]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || selectedScopes.length === 0) return;

    setIsSubmitting(true);
    setError(null);

    try {
      let expiresAt: string | undefined;
      if (expiresIn !== 'never') {
        const now = new Date();
        switch (expiresIn) {
          case '30d':
            now.setDate(now.getDate() + 30);
            break;
          case '90d':
            now.setDate(now.getDate() + 90);
            break;
          case '1y':
            now.setFullYear(now.getFullYear() + 1);
            break;
        }
        expiresAt = now.toISOString();
      }

      const data: CreateApiKeyRequest = {
        name: name.trim(),
        scopes: selectedScopes,
        accountIds: allAccounts ? [] : selectedAccounts,
        expiresAt,
      };

      const result = await createApiKey(data);
      onCreated(result.key, result.apiKey);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create API key');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setName('');
    setSelectedScopes(['operations:read', 'operations:create', 'accounts:read', 'categories:read', 'counterparties:read', 'payment-methods:read']);
    setSelectedAccounts([]);
    setAllAccounts(true);
    setExpiresIn('never');
    setError(null);
  };

  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-2xl mx-4 my-8 max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <div className="sticky top-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
              {t('apiKeys.createKey')}
            </h2>
          </div>

          <div className="p-6 space-y-6">
            {error && (
              <div className="bg-rose-50 dark:bg-rose-900/30 border border-rose-200 dark:border-rose-800 rounded-lg p-3 text-rose-700 dark:text-rose-300 text-sm">
                {error}
              </div>
            )}

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('apiKeys.name')} *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                placeholder={t('apiKeys.namePlaceholder')}
                required
                autoFocus
              />
            </div>

            {/* Scopes */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                {t('apiKeys.scopes')} *
              </label>
              <div className="space-y-4">
                {Object.entries(scopesByCategory).map(([category, scopes]) => (
                  <div key={category} className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-slate-700 dark:text-slate-200 text-sm">
                        {t(`apiKeys.scopeCategories.${category}`)}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleCategoryToggleAll(category, scopes)}
                        className="text-xs text-violet-600 dark:text-violet-400 hover:underline"
                      >
                        {scopes.every(s => selectedScopes.includes(s)) ? 'Désélectionner tout' : 'Tout sélectionner'}
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {scopes.map(scope => (
                        <label key={scope} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedScopes.includes(scope)}
                            onChange={() => handleScopeToggle(scope)}
                            className="w-4 h-4 text-violet-600 bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600 rounded focus:ring-violet-500"
                          />
                          <span className="text-sm text-slate-600 dark:text-slate-300">
                            {t(`apiKeys.availableScopes.${scope}`)}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Accounts */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                {t('apiKeys.accounts')}
              </label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="accountAccess"
                    checked={allAccounts}
                    onChange={() => setAllAccounts(true)}
                    className="w-4 h-4 text-violet-600 bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600 focus:ring-violet-500"
                  />
                  <span className="text-sm text-slate-600 dark:text-slate-300">
                    {t('apiKeys.allAccounts')}
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="accountAccess"
                    checked={!allAccounts}
                    onChange={() => setAllAccounts(false)}
                    className="w-4 h-4 text-violet-600 bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600 focus:ring-violet-500"
                  />
                  <span className="text-sm text-slate-600 dark:text-slate-300">
                    Comptes spécifiques
                  </span>
                </label>
                {!allAccounts && (
                  <div className="ml-6 mt-2 space-y-1">
                    {accounts.map(account => (
                      <label key={account.id} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedAccounts.includes(account.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedAccounts(prev => [...prev, account.id]);
                            } else {
                              setSelectedAccounts(prev => prev.filter(id => id !== account.id));
                            }
                          }}
                          className="w-4 h-4 text-violet-600 bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600 rounded focus:ring-violet-500"
                        />
                        <span className="text-sm text-slate-600 dark:text-slate-300">
                          {account.name}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Expiration */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                {t('apiKeys.expiresAt')}
              </label>
              <select
                value={expiresIn}
                onChange={(e) => setExpiresIn(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
              >
                <option value="never">{t('apiKeys.noExpiration')}</option>
                <option value="30d">30 jours</option>
                <option value="90d">90 jours</option>
                <option value="1y">1 an</option>
              </select>
            </div>
          </div>

          <div className="sticky bottom-0 bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 px-6 py-4 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !name.trim() || selectedScopes.length === 0}
              className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? t('common.saving') : t('apiKeys.createKey')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface KeyCreatedModalProps {
  isOpen: boolean;
  apiKey: string;
  onClose: () => void;
}

const KeyCreatedModal = ({ isOpen, apiKey, onClose }: KeyCreatedModalProps) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(apiKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" />
      <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        <div className="text-center mb-4">
          <div className="w-12 h-12 mx-auto mb-3 bg-emerald-100 dark:bg-emerald-900/50 rounded-full flex items-center justify-center">
            <CheckIcon />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            {t('apiKeys.keyCreated')}
          </h2>
        </div>

        <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mb-4">
          <p className="text-sm text-amber-700 dark:text-amber-300">
            ⚠️ {t('apiKeys.keyCreatedWarning')}
          </p>
        </div>

        <div className="bg-slate-100 dark:bg-slate-700 rounded-lg p-3 mb-4">
          <code className="text-sm font-mono text-slate-700 dark:text-slate-200 break-all">
            {apiKey}
          </code>
        </div>

        <div className="flex justify-end gap-3">
          <button
            onClick={handleCopy}
            className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
          >
            {copied ? <CheckIcon /> : <CopyIcon />}
            {copied ? t('apiKeys.keyCopied') : t('apiKeys.copyKey')}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  confirmVariant?: 'danger' | 'warning';
  onConfirm: () => void;
  onClose: () => void;
  isLoading?: boolean;
}

const ConfirmModal = ({
  isOpen,
  title,
  message,
  confirmLabel,
  confirmVariant = 'danger',
  onConfirm,
  onClose,
  isLoading = false,
}: ConfirmModalProps) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const buttonStyles = {
    danger: 'bg-rose-600 hover:bg-rose-700',
    warning: 'bg-amber-600 hover:bg-amber-700',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-4">
          {title}
        </h2>
        <p className="text-slate-600 dark:text-slate-300 mb-6">
          {message}
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className={`px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 ${buttonStyles[confirmVariant]}`}
          >
            {isLoading ? t('common.loading') : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const DocumentationSection = () => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-700/50 rounded-xl transition-colors"
      >
        <div className="flex items-center gap-2">
          <DocumentIcon />
          <span className="font-semibold text-slate-700 dark:text-slate-200">
            {t('apiKeys.documentation')}
          </span>
        </div>
        {isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 border-t border-slate-100 dark:border-slate-700 pt-4 space-y-4 text-sm">
          <p className="text-slate-500 dark:text-slate-400">
            {t('apiKeys.documentationDescription')}
          </p>

          <div>
            <h4 className="font-medium text-slate-700 dark:text-slate-200 mb-1">
              {t('apiKeys.baseUrl')}
            </h4>
            <code className="block bg-slate-100 dark:bg-slate-700 px-3 py-2 rounded-lg text-sm font-mono text-slate-700 dark:text-slate-200">
              {window.location.origin}/api/external/v1
            </code>
          </div>

          <div>
            <h4 className="font-medium text-slate-700 dark:text-slate-200 mb-1">
              {t('apiKeys.authentication')}
            </h4>
            <p className="text-slate-500 dark:text-slate-400 mb-2">
              {t('apiKeys.authenticationDescription')}
            </p>
            <code className="block bg-slate-100 dark:bg-slate-700 px-3 py-2 rounded-lg text-sm font-mono text-slate-700 dark:text-slate-200">
              X-API-Key: bud_live_xxxxxxxxxxxxx
            </code>
          </div>

          <div>
            <h4 className="font-medium text-slate-700 dark:text-slate-200 mb-2">
              Endpoints disponibles
            </h4>
            <div className="space-y-2">
              <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-2">
                <code className="text-emerald-600 dark:text-emerald-400">GET</code>
                <code className="ml-2 text-slate-600 dark:text-slate-300">/health</code>
                <span className="ml-2 text-slate-400 text-xs">- Vérifier l'état de l'API</span>
              </div>
              <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-2">
                <code className="text-emerald-600 dark:text-emerald-400">GET</code>
                <code className="ml-2 text-slate-600 dark:text-slate-300">/me</code>
                <span className="ml-2 text-slate-400 text-xs">- Informations sur la clé API</span>
              </div>
              <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-2">
                <code className="text-emerald-600 dark:text-emerald-400">GET</code>
                <code className="ml-2 text-slate-600 dark:text-slate-300">/accounts</code>
                <span className="ml-2 text-slate-400 text-xs">- Liste des comptes autorisés</span>
              </div>
              <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-2">
                <code className="text-emerald-600 dark:text-emerald-400">GET</code>
                <code className="ml-2 text-slate-600 dark:text-slate-300">/categories</code>
                <span className="ml-2 text-slate-400 text-xs">- Liste des catégories</span>
              </div>
              <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-2">
                <code className="text-emerald-600 dark:text-emerald-400">GET</code>
                <code className="ml-2 text-slate-600 dark:text-slate-300">/counterparties</code>
                <span className="ml-2 text-slate-400 text-xs">- Liste des tiers</span>
              </div>
              <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-2">
                <code className="text-emerald-600 dark:text-emerald-400">GET</code>
                <code className="ml-2 text-slate-600 dark:text-slate-300">/payment-methods</code>
                <span className="ml-2 text-slate-400 text-xs">- Liste des modes de paiement</span>
              </div>
              <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-2">
                <code className="text-emerald-600 dark:text-emerald-400">GET</code>
                <code className="ml-2 text-slate-600 dark:text-slate-300">/projects</code>
                <span className="ml-2 text-slate-400 text-xs">- Liste des projets</span>
              </div>
              <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-2">
                <code className="text-emerald-600 dark:text-emerald-400">GET</code>
                <code className="ml-2 text-slate-600 dark:text-slate-300">/accounts/:id/operations</code>
                <span className="ml-2 text-slate-400 text-xs">- Liste des opérations d'un compte</span>
              </div>
              <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-2">
                <code className="text-blue-600 dark:text-blue-400">POST</code>
                <code className="ml-2 text-slate-600 dark:text-slate-300">/accounts/:id/operations</code>
                <span className="ml-2 text-slate-400 text-xs">- {t('apiKeys.createOperation')}</span>
              </div>
            </div>

            {/* Create operation documentation */}
            <div className="mt-6 border-t border-slate-200 dark:border-slate-600 pt-4">
              <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                {t('apiKeys.createOperationTitle')}
              </h4>
              
              {/* Fields table */}
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-600">
                      <th className="text-left py-2 pr-2 text-slate-600 dark:text-slate-400 font-medium">{t('apiKeys.fieldName')}</th>
                      <th className="text-left py-2 pr-2 text-slate-600 dark:text-slate-400 font-medium">{t('apiKeys.fieldType')}</th>
                      <th className="text-left py-2 pr-2 text-slate-600 dark:text-slate-400 font-medium">{t('apiKeys.fieldRequired')}</th>
                      <th className="text-left py-2 text-slate-600 dark:text-slate-400 font-medium">{t('apiKeys.fieldDescription')}</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-600 dark:text-slate-300">
                    <tr className="border-b border-slate-100 dark:border-slate-700">
                      <td className="py-2 pr-2"><code className="text-pink-600 dark:text-pink-400">amount</code></td>
                      <td className="py-2 pr-2">number</td>
                      <td className="py-2 pr-2">✓</td>
                      <td className="py-2">{t('apiKeys.fields.amount')}</td>
                    </tr>
                    <tr className="border-b border-slate-100 dark:border-slate-700">
                      <td className="py-2 pr-2"><code className="text-pink-600 dark:text-pink-400">direction</code></td>
                      <td className="py-2 pr-2">string</td>
                      <td className="py-2 pr-2">✓</td>
                      <td className="py-2">{t('apiKeys.fields.direction')}</td>
                    </tr>
                    <tr className="border-b border-slate-100 dark:border-slate-700">
                      <td className="py-2 pr-2"><code className="text-pink-600 dark:text-pink-400">paymentDate</code></td>
                      <td className="py-2 pr-2">string</td>
                      <td className="py-2 pr-2">✓</td>
                      <td className="py-2">{t('apiKeys.fields.paymentDate')}</td>
                    </tr>
                    <tr className="border-b border-slate-100 dark:border-slate-700">
                      <td className="py-2 pr-2"><code className="text-pink-600 dark:text-pink-400">counterparty</code></td>
                      <td className="py-2 pr-2">string</td>
                      <td className="py-2 pr-2">✓</td>
                      <td className="py-2">{t('apiKeys.fields.counterparty')}</td>
                    </tr>
                    <tr className="border-b border-slate-100 dark:border-slate-700">
                      <td className="py-2 pr-2"><code className="text-pink-600 dark:text-pink-400">paymentMethod</code></td>
                      <td className="py-2 pr-2">string</td>
                      <td className="py-2 pr-2">✓</td>
                      <td className="py-2">{t('apiKeys.fields.paymentMethod')}</td>
                    </tr>
                    <tr className="border-b border-slate-100 dark:border-slate-700">
                      <td className="py-2 pr-2"><code className="text-pink-600 dark:text-pink-400">category</code></td>
                      <td className="py-2 pr-2">string</td>
                      <td className="py-2 pr-2">✓</td>
                      <td className="py-2">{t('apiKeys.fields.category')}</td>
                    </tr>
                    <tr className="border-b border-slate-100 dark:border-slate-700">
                      <td className="py-2 pr-2"><code className="text-pink-600 dark:text-pink-400">accountingDate</code></td>
                      <td className="py-2 pr-2">string</td>
                      <td className="py-2 pr-2"></td>
                      <td className="py-2">{t('apiKeys.fields.accountingDate')}</td>
                    </tr>
                    <tr className="border-b border-slate-100 dark:border-slate-700">
                      <td className="py-2 pr-2"><code className="text-pink-600 dark:text-pink-400">project</code></td>
                      <td className="py-2 pr-2">string</td>
                      <td className="py-2 pr-2"></td>
                      <td className="py-2">{t('apiKeys.fields.project')}</td>
                    </tr>
                    <tr className="border-b border-slate-100 dark:border-slate-700">
                      <td className="py-2 pr-2"><code className="text-pink-600 dark:text-pink-400">reference</code></td>
                      <td className="py-2 pr-2">string</td>
                      <td className="py-2 pr-2"></td>
                      <td className="py-2">{t('apiKeys.fields.reference')}</td>
                    </tr>
                    <tr className="border-b border-slate-100 dark:border-slate-700">
                      <td className="py-2 pr-2"><code className="text-pink-600 dark:text-pink-400">comment</code></td>
                      <td className="py-2 pr-2">string</td>
                      <td className="py-2 pr-2"></td>
                      <td className="py-2">{t('apiKeys.fields.comment')}</td>
                    </tr>
                    <tr className="border-b border-slate-100 dark:border-slate-700">
                      <td className="py-2 pr-2"><code className="text-pink-600 dark:text-pink-400">paymentDetails</code></td>
                      <td className="py-2 pr-2">string</td>
                      <td className="py-2 pr-2"></td>
                      <td className="py-2">{t('apiKeys.fields.paymentDetails')}</td>
                    </tr>
                    <tr className="border-b border-slate-100 dark:border-slate-700">
                      <td className="py-2 pr-2"><code className="text-pink-600 dark:text-pink-400">checkbookId</code></td>
                      <td className="py-2 pr-2">string</td>
                      <td className="py-2 pr-2"></td>
                      <td className="py-2">{t('apiKeys.fields.checkbookId')}</td>
                    </tr>
                    <tr className="border-b border-slate-100 dark:border-slate-700">
                      <td className="py-2 pr-2"><code className="text-pink-600 dark:text-pink-400">status</code></td>
                      <td className="py-2 pr-2">string</td>
                      <td className="py-2 pr-2"></td>
                      <td className="py-2">{t('apiKeys.fields.status')}</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-2"><code className="text-pink-600 dark:text-pink-400">createCounterpartyIfNotFound</code></td>
                      <td className="py-2 pr-2">boolean</td>
                      <td className="py-2 pr-2"></td>
                      <td className="py-2">{t('apiKeys.fields.createCounterpartyIfNotFound')}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Example JSON */}
              <h5 className="text-xs font-medium text-slate-600 dark:text-slate-400 mt-4 mb-2">
                {t('apiKeys.createOperationExample')}
              </h5>
              <pre className="bg-slate-800 dark:bg-slate-900 text-slate-100 rounded-lg p-3 text-xs overflow-x-auto">
{`{
  "amount": 25.50,
  "direction": "debit",
  "paymentDate": "2026-08-27",
  "counterparty": "Carrefour",
  "paymentMethod": "Carte bancaire",
  "category": "Alimentation",
  "project": "Rénovation cuisine",
  "reference": "FACT-001",
  "comment": "Courses hebdomadaires",
  "paymentDetails": "Visa *1234",
  "createCounterpartyIfNotFound": true
}`}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default function ApiKeysPage() {
  const { t } = useTranslation();
  const { fetchAccounts } = useAccountStore();
  
  const [apiKeys, setApiKeys] = useState<ApiKeyDisplay[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<{
    type: 'revoke' | 'delete';
    keyId: string;
    keyName: string;
  } | null>(null);
  const [isActionLoading, setIsActionLoading] = useState(false);

  const loadApiKeys = useCallback(async () => {
    try {
      setIsLoading(true);
      const keys = await listApiKeys();
      setApiKeys(keys);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load API keys');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadApiKeys();
    fetchAccounts();
  }, [loadApiKeys, fetchAccounts]);

  const handleKeyCreated = (key: string, apiKey: ApiKeyDisplay) => {
    setShowCreateModal(false);
    setCreatedKey(key);
    setApiKeys(prev => [apiKey, ...prev]);
  };

  const handleRevoke = (keyId: string) => {
    const key = apiKeys.find(k => k.id === keyId);
    if (key) {
      setConfirmAction({ type: 'revoke', keyId, keyName: key.name });
    }
  };

  const handleDelete = (keyId: string) => {
    const key = apiKeys.find(k => k.id === keyId);
    if (key) {
      setConfirmAction({ type: 'delete', keyId, keyName: key.name });
    }
  };

  const handleConfirmAction = async () => {
    if (!confirmAction) return;

    setIsActionLoading(true);
    try {
      if (confirmAction.type === 'revoke') {
        await revokeApiKey(confirmAction.keyId);
        setApiKeys(prev => prev.map(k => 
          k.id === confirmAction.keyId ? { ...k, status: 'revoked' as const } : k
        ));
      } else {
        await deleteApiKey(confirmAction.keyId);
        setApiKeys(prev => prev.filter(k => k.id !== confirmAction.keyId));
      }
      setConfirmAction(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setIsActionLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
            {t('apiKeys.title')}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            {t('apiKeys.description')}
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
        >
          <PlusIcon />
          <span>{t('apiKeys.createKey')}</span>
        </button>
      </div>

      {/* Error display */}
      {error && (
        <div className="bg-rose-50 dark:bg-rose-900/30 border border-rose-200 dark:border-rose-800 rounded-lg p-4 flex items-center justify-between">
          <p className="text-rose-700 dark:text-rose-300">{error}</p>
          <button
            onClick={() => setError(null)}
            className="text-rose-500 dark:text-rose-400 hover:text-rose-700 dark:hover:text-rose-300"
          >
            ✕
          </button>
        </div>
      )}

      {/* Documentation */}
      <DocumentationSection />

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-violet-200 dark:border-violet-800 border-t-violet-600 dark:border-t-violet-400 rounded-full animate-spin" />
        </div>
      )}

      {/* Empty state */}
      {!isLoading && apiKeys.length === 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center">
            <KeyIcon />
          </div>
          <h2 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
            {t('apiKeys.noKeys')}
          </h2>
          <p className="text-slate-500 dark:text-slate-400 mb-6">
            {t('apiKeys.noKeysDescription')}
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
          >
            <PlusIcon />
            <span>{t('apiKeys.createKey')}</span>
          </button>
        </div>
      )}

      {/* API Keys list */}
      {!isLoading && apiKeys.length > 0 && (
        <div className="space-y-3">
          {apiKeys.map(apiKey => (
            <ApiKeyCard
              key={apiKey.id}
              apiKey={apiKey}
              onRevoke={handleRevoke}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      <CreateKeyModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={handleKeyCreated}
      />

      <KeyCreatedModal
        isOpen={!!createdKey}
        apiKey={createdKey || ''}
        onClose={() => setCreatedKey(null)}
      />

      <ConfirmModal
        isOpen={!!confirmAction}
        title={confirmAction?.type === 'revoke' ? t('apiKeys.revoke') : t('apiKeys.delete')}
        message={confirmAction?.type === 'revoke' 
          ? t('apiKeys.revokeConfirm')
          : t('apiKeys.deleteConfirm')
        }
        confirmLabel={confirmAction?.type === 'revoke' ? t('apiKeys.revoke') : t('apiKeys.delete')}
        confirmVariant={confirmAction?.type === 'revoke' ? 'warning' : 'danger'}
        onConfirm={handleConfirmAction}
        onClose={() => setConfirmAction(null)}
        isLoading={isActionLoading}
      />
    </div>
  );
}
